import { defineStore } from "pinia";
import api from "../services/api.ts";

export interface IPAlias {
  id: string;
  name: string;
  ip: string;
}

let fetchPromise: Promise<void> | null = null;

export const useIPAliasStore = defineStore("ipAliases", {
  state: () => ({
    aliases: [] as IPAlias[],
    loaded: false,
  }),

  getters: {
    /** Find aliases matching a query string (searches both name and IP). */
    search:
      (state) =>
      (query: string): IPAlias[] => {
        if (!query.trim()) return state.aliases;
        const q = query.toLowerCase();
        return state.aliases.filter((a) => a.name.toLowerCase().includes(q) || a.ip.toLowerCase().includes(q));
      },

    /** Look up the IP for an exact alias name (case-insensitive). */
    resolveAlias:
      (state) =>
      (name: string): string | null => {
        const match = state.aliases.find((a) => a.name.toLowerCase() === name.toLowerCase());
        return match?.ip ?? null;
      },

    /** Check if an alias name already exists. */
    hasAlias:
      (state) =>
      (name: string): boolean => {
        return state.aliases.some((a) => a.name.toLowerCase() === name.toLowerCase());
      },
  },

  actions: {
    async fetchAliases() {
      if (this.loaded) return;
      if (fetchPromise) return fetchPromise;

      fetchPromise = (async () => {
        try {
          const response = await api.get("/sessions/current/ip-aliases");
          this.aliases = response.data;
          this.loaded = true;
        } catch {
          // If fetch fails, keep existing state
        } finally {
          fetchPromise = null;
        }
      })();

      return fetchPromise;
    },

    async addAlias(name: string, ip: string) {
      const trimmedName = name.trim();
      const trimmedIp = ip.trim();
      if (!trimmedName || !trimmedIp) return;

      try {
        const response = await api.post("/sessions/current/ip-aliases", { name: trimmedName, ip: trimmedIp });
        const created: IPAlias = response.data;
        const existing = this.aliases.findIndex((a) => a.name.toLowerCase() === trimmedName.toLowerCase());
        if (existing >= 0) {
          this.aliases[existing] = created;
        } else {
          this.aliases.push(created);
        }
      } catch {
        // Silently fail: the dialog will show the error
      }
    },

    async removeAlias(name: string) {
      const alias = this.aliases.find((a) => a.name.toLowerCase() === name.toLowerCase());
      if (!alias) return;

      try {
        await api.delete(`/sessions/current/ip-aliases/${alias.id}`);
        this.aliases = this.aliases.filter((a) => a.id !== alias.id);
      } catch {
        // Silently fail
      }
    },

    async updateAlias(oldName: string, newName: string, newIp: string) {
      const alias = this.aliases.find((a) => a.name.toLowerCase() === oldName.toLowerCase());
      if (!alias) return;

      try {
        const response = await api.put(`/sessions/current/ip-aliases/${alias.id}`, {
          name: newName.trim(),
          ip: newIp.trim(),
        });
        const updated: IPAlias = response.data;
        const index = this.aliases.findIndex((a) => a.id === alias.id);
        if (index >= 0) {
          this.aliases[index] = updated;
        }
      } catch {
        // Silently fail
      }
    },
  },
});
